import { apiRequest } from '../api/client';
import { localPreviewHeaders } from '../store/feedback-local-preview';

/**
 * HQ Feedback client. Feedback is LOCAL to HQ: reports are stored by the HQ Feedback function in HQ's own table and managed in HQ's
 * own Owner inbox. Every call goes through HQ's apiRequest(), so it carries the HQ Cognito token:
 *   POST /feedback               submit (any signed-in HQ person)
 *   GET  /feedback?status=       the Owner inbox list   (Owner only, enforced by the server)
 *   PUT  /feedback/{id}/status   complete / reopen      (Owner only, enforced by the server)
 *
 * TRUST MODEL: the client sends ONLY { type, details, clientRequestId, pagePath } to submit. The server takes who sent it from the
 * verified sign-in and when from its own clock. The body can never name a user, a date or a status.
 */

export type FeedbackType = 'Bug' | 'Idea';
export type FeedbackStatus = 'open' | 'completed';

export interface FeedbackSubmitInput {
  type: FeedbackType;
  details: string;
  clientRequestId: string;
  pagePath: string;
}

export interface FeedbackSubmitResult {
  id: string;
  reference: string;
  createdAt: string;
  replayed: boolean;
}

/** One report as the Owner inbox sees it. */
export interface FeedbackReport {
  id: string;
  reference: string;
  type: FeedbackType;
  details: string;
  status: FeedbackStatus;
  submittedBy: { userId: string; name: string; email: string; role: string };
  pageContext: { path: string };
  createdAt: string;
  statusChangedAt: string | null;
  statusChangedBy: { userId: string; name: string } | null;
}

export interface FeedbackListResult {
  items: FeedbackReport[];
  nextToken: string | null;
  status: FeedbackStatus;
}

/**
 * Whether to SHOW the Owner Inbox tab. This is a UI hint from the same rule the server uses (the Cognito `owner` group, as for
 * Notes and Attention in AppLayout). It grants nothing: the server enforces Owner on every list and status call, so an admin who
 * is not the Owner sees no tab and gets a 403 from a direct call.
 */
export function showsFeedbackInbox(groups: string[] | undefined | null): boolean {
  return Array.isArray(groups) && groups.includes('owner');
}

export async function submitFeedback(input: FeedbackSubmitInput): Promise<FeedbackSubmitResult> {
  return apiRequest('/feedback', { method: 'POST', body: JSON.stringify(input), headers: localPreviewHeaders() });
}

export async function listFeedback(opts: { status: FeedbackStatus; nextToken?: string | null }): Promise<FeedbackListResult> {
  const qs = new URLSearchParams({ status: opts.status });
  if (opts.nextToken) qs.set('nextToken', opts.nextToken);
  const res = await apiRequest(`/feedback?${qs.toString()}`, { headers: localPreviewHeaders() });
  return { items: Array.isArray(res?.items) ? res.items : [], nextToken: res?.nextToken ?? null, status: res?.status ?? opts.status };
}

export async function setFeedbackStatus(id: string, status: FeedbackStatus): Promise<{ report: FeedbackReport; changed: boolean }> {
  return apiRequest(`/feedback/${encodeURIComponent(id)}/status`, { method: 'PUT', body: JSON.stringify({ status }), headers: localPreviewHeaders() });
}

/** A short, user-facing reason for a failed call. Never includes server detail or the user's text. */
export function describeFeedbackError(e: unknown): string {
  const status = (e as { status?: number })?.status;
  if (status === 403) return 'You do not have access to do that.';
  if (status === 401) return 'Your session has expired. Sign in again, then send your feedback.';
  if (status === 400 || status === 413) return 'That could not be accepted. Check the text and try again.';
  if (status === 404) return 'That report could not be found. Refresh and try again.';
  if (status === 409) return 'This report changed while you were working. Refresh and try again.';
  if (typeof status === 'number' && status >= 500) return 'Feedback is temporarily unavailable.';
  return 'We could not reach the server.';
}
